import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { ArrowLeft, ArrowRight, CheckCircle, User, Trophy, Share2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useUser } from '@/contexts/UserContext';
import { userService } from '@/services/userService';

interface AthleteOnboardingProps {
  onComplete: (data: AthleteFormData) => void;
  onBack: () => void;
  isCompletion?: boolean;
}

interface AthleteFormData {
  name: string;
  sport: string;
  talentType: string;
  bio: string;
  location: string;
  socialProfiles: {
    instagram: string;
    twitter: string;
    tiktok: string;
    youtube: string;
  };
  achievements: string[];
  status: string;
  agentContact: {
    name: string;
    email: string;
    phone: string;
  };
}

const initialFormData: AthleteFormData = {
  name: '',
  sport: '',
  talentType: 'athlete',
  bio: '',
  location: '',
  socialProfiles: {
    instagram: '',
    twitter: '',
    tiktok: '',
    youtube: '',
  },
  achievements: [],
  status: 'Active',
  agentContact: {
    name: '',
    email: '',
    phone: '',
  },
};

const activityOptions = {
  athlete: [
    'Basketball', 'Football', 'Soccer', 'Baseball', 'Tennis', 'Golf',
    'Swimming', 'Track & Field', 'Hockey', 'Volleyball', 'Boxing', 'MMA',
    'Wrestling', 'Gymnastics', 'Figure Skating', 'Skiing', 'Snowboarding',
    'Surfing', 'Skateboarding', 'BMX', 'Esports', 'Other'
  ],
  creator: [
    'YouTube', 'Instagram', 'TikTok', 'Twitch', 'Twitter/X', 'Facebook',
    'LinkedIn', 'Podcast', 'Blog', 'Newsletter', 'Multi-Platform', 'Other'
  ],
  entertainer: [
    'Actor', 'Musician', 'Singer', 'Comedian', 'Dancer', 'DJ', 'Producer',
    'Voice Actor', 'Theater', 'Magician', 'Influencer', 'Model', 'Other'
  ],
  other: [
    'Business', 'Technology', 'Healthcare', 'Education', 'Finance', 'Legal',
    'Real Estate', 'Consulting', 'Marketing', 'Sales', 'Other'
  ]
};

const statusOptions = {
  athlete: ['Active', 'Retired', 'Amateur', 'Professional', 'College', 'Semi-Pro'],
  creator: ['Active', 'On Break', 'Growing', 'Established', 'Verified', 'Rising'],
  entertainer: ['Active', 'Between Projects', 'Seeking Representation', 'Established', 'Emerging'],
  other: ['Active', 'Freelance', 'Employed', 'Consulting', 'Entrepreneur', 'Seeking Opportunities']
};

const achievementPlaceholders = {
  athlete: 'e.g., Olympic Gold Medal, Championship Winner, Record Holder',
  creator: 'e.g., 1M Subscribers, Viral Video, Brand Partnership with Nike',
  entertainer: 'e.g., Award Winner, Notable Performance in Broadway, Major Film Role',
  other: 'e.g., Industry Award, Leadership Recognition, Professional Certification'
};

const activityLabels = {
  athlete: 'Sport',
  creator: 'Primary Platform',
  entertainer: 'Entertainment Type',
  other: 'Professional Field'
};

const talentTypeOptions = [
  { value: 'athlete', label: 'Athlete' },
  { value: 'creator', label: 'Content Creator' },
  { value: 'entertainer', label: 'Entertainer' },
  { value: 'other', label: 'Other' }
];

const steps = [
  { id: 1, title: 'Basic Info', icon: User },
  { id: 2, title: 'Social Profiles', icon: Share2 },
  { id: 3, title: 'Career Info', icon: Trophy },
  { id: 4, title: 'Review', icon: CheckCircle }
];

export const TalentOnboarding: React.FC<AthleteOnboardingProps> = ({
  onComplete,
  onBack,
  isCompletion = false
}) => {
  const { currentUser } = useUser();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<AthleteFormData>(initialFormData);
  const [newAchievement, setNewAchievement] = useState('');
  const [loading, setLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  // Load existing data when in completion mode
  useEffect(() => {
    const loadExistingData = async () => {
      if (!isCompletion || !currentUser?.user_id) return;

      try {
        const profile = await userService.getProfileByUserId(currentUser.user_id);
        if (profile) {
          const socialMedia = (profile.talent as any)?.social_media || {};
          const agentContact = (profile.talent as any)?.agent_contact || {};
          
          const existingData: AthleteFormData = {
            name: profile.talent?.name || '',
            sport: profile.talent?.sport || '',
            talentType: profile.talent?.talent_type || 'athlete',
            bio: profile.bio || '',
            location: profile.location || '',
            socialProfiles: {
              instagram: socialMedia?.instagram || '',
              twitter: socialMedia?.twitter || '',
              tiktok: socialMedia?.tiktok || '',
              youtube: socialMedia?.youtube || '',
            },
            achievements: Array.isArray(profile.talent?.achievements) ? profile.talent.achievements : [],
            status: profile.talent?.status || 'Active',
            agentContact: {
              name: agentContact?.name || '',
              email: agentContact?.email || '',
              phone: agentContact?.phone || '',
            },
          };
          setFormData(existingData);
        }
      } catch (error) {
        console.error('Error loading existing data:', error);
      }
    };

    loadExistingData();
  }, [isCompletion, currentUser?.user_id]);

  const progress = (currentStep / steps.length) * 100;

  const updateFormData = (field: string, value: any) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...(prev[parent as keyof AthleteFormData] as any),
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  const generateProfile = async () => {
    if (!formData.name || !formData.sport) {
      toast.error('Please fill in name and sport before generating profile');
      return;
    }

    setIsGenerating(true);
    try {
      const promptContext = {
        athlete: 'professional athlete with focus on sports achievements and competition',
        creator: 'content creator with focus on digital platforms and audience engagement',
        entertainer: 'entertainer with focus on performances, productions, and artistic work',
        other: 'professional with focus on industry expertise and achievements'
      };

      const { data, error } = await supabase.functions.invoke('generate-profile', {
        body: {
          accountType: 'athlete',
          talentType: formData.talentType,
          name: formData.name,
          sport: formData.sport,
          location: formData.location,
          socialHandle: formData.socialProfiles.instagram,
          additionalInfo: formData.bio,
          context: promptContext[formData.talentType as keyof typeof promptContext]
        }
      });

      if (error) throw error;

      if (data.success) {
        const profile = data.profile;
        
        // Update form data with generated content
        setFormData(prev => ({
          ...prev,
          bio: profile.bio || prev.bio,
          achievements: profile.achievements || prev.achievements,
        }));

        toast.success('Profile generated successfully!');
      } else {
        throw new Error(data.error);
      }
    } catch (error) {
      console.error('Error generating profile:', error);
      toast.error('Failed to generate profile. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const addAchievement = () => {
    if (newAchievement.trim()) {
      updateFormData('achievements', [...formData.achievements, newAchievement.trim()]);
      setNewAchievement('');
    }
  };

  const removeAchievement = (index: number) => {
    updateFormData('achievements', formData.achievements.filter((_, i) => i !== index));
  };

  const nextStep = () => {
    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error('Please sign in first');
        return;
      }

      // Get or create profile
      let { data: profile } = await supabase
        .from('profiles')
        .select('id')
        .eq('user_id', user.id)
        .maybeSingle();

      if (!profile) {
        const { data: newProfile, error: profileError } = await supabase
          .from('profiles')
          .insert({
            user_id: user.id,
            email: user.email || '',
            display_name: formData.name || user.email?.split('@')[0] || 'Talent',
            account_type: 'talent',
            setup_completed: true
          })
          .select('id')
          .single();

        if (profileError) {
          console.error('Error creating profile:', profileError);
          toast.error('Failed to create profile. Please try again.');
          return;
        }
        profile = newProfile;
      }

      // Check if talent record already exists
      const { data: existingTalent } = await supabase
        .from('talents')
        .select('id')
        .eq('profile_id', profile.id)
        .maybeSingle();

      // Prepare talent data - allow empty values
      const talentData = {
        profile_id: profile.id,
        name: formData.name || 'Talent',
        sport: formData.sport || 'Not specified',
        talent_type: (formData.talentType || 'athlete') as 'athlete' | 'creator' | 'entertainer' | 'other',
        status: formData.status || 'Active',
        achievements: formData.achievements.length > 0 ? formData.achievements : [],
        agent_contact: formData.agentContact,
        social_profiles: formData.socialProfiles,
      };

      let error;
      if (existingTalent) {
        // Update existing talent record
        const { error: updateError } = await supabase
          .from('talents')
          .update(talentData)
          .eq('id', existingTalent.id);
        error = updateError;
      } else {
        // Insert new talent record
        const { error: insertError } = await supabase
          .from('talents')
          .insert(talentData);
        error = insertError;
      }

      if (error) {
        console.error('Error saving talent:', error);
        toast.error('Failed to save talent profile');
        return;
      }

      // Update profile with bio, location, email, display_name and account_type
      const { data: { user: authUser } } = await supabase.auth.getUser();
      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          email: authUser?.email || '',
          display_name: formData.name || authUser?.email?.split('@')[0] || 'Talent',
          bio: formData.bio || '',
          location: formData.location || '',
          account_type: 'talent',
          setup_completed: true
        })
        .eq('id', profile.id);

      if (profileError) {
        console.error('Error updating profile:', profileError);
        toast.error('Failed to update profile settings');
        return;
      }

      // Small delay to ensure database consistency before navigation
      await new Promise(resolve => setTimeout(resolve, 100));

      toast.success('Talent profile created successfully!');
      onComplete(formData);
    } catch (error) {
      console.error('Error:', error);
      toast.error('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Basic Information</h3>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={generateProfile}
                disabled={isGenerating || !formData.name || !formData.sport}
                className="flex items-center gap-2"
              >
                {isGenerating ? (
                  <>
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-r-transparent" />
                    Generating...
                  </>
                ) : (
                  <>
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                    Auto-Generate Profile
                  </>
                )}
              </Button>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="name">Full Name *</Label>
              <Input
                id="name"
                placeholder="e.g., Serena Williams"
                value={formData.name}
                onChange={(e) => updateFormData('name', e.target.value)}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="talentType">I am a *</Label>
              <Select value={formData.talentType} onValueChange={(value) => {
                updateFormData('talentType', value);
                // Auto-populate sport for non-athletes
                if (value !== 'athlete') {
                  const sportMap = {
                    creator: 'Content Creation',
                    entertainer: 'Entertainment',
                    other: 'Professional'
                  };
                  updateFormData('sport', sportMap[value as keyof typeof sportMap]);
                }
              }}>
                <SelectTrigger>
                  <SelectValue placeholder="Select your type" />
                </SelectTrigger>
                <SelectContent>
                  {talentTypeOptions.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {formData.talentType === 'athlete' && (
              <div className="space-y-2">
                <Label htmlFor="sport">Sport *</Label>
                <Select value={formData.sport} onValueChange={(value) => updateFormData('sport', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select your sport" />
                  </SelectTrigger>
                  <SelectContent>
                    {activityOptions.athlete.map((option) => (
                      <SelectItem key={option} value={option}>
                        {option}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="bio">Bio *</Label>
              <Textarea
                id="bio"
                placeholder="Tell us about yourself, your career, and what drives you..."
                value={formData.bio}
                onChange={(e) => updateFormData('bio', e.target.value)}
                rows={4}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="location">Location</Label>
              <Input
                id="location"
                placeholder="e.g., Los Angeles, CA"
                value={formData.location}
                onChange={(e) => updateFormData('location', e.target.value)}
              />
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div className="text-center space-y-2 mb-6">
              <Share2 className="h-8 w-8 text-primary mx-auto" />
              <h3 className="text-lg font-semibold">Social Media Profiles</h3>
              <p className="text-muted-foreground text-sm">
                Connect your social media accounts to track your online presence
              </p>
            </div>
            
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="instagram">Instagram Handle</Label>
                <Input
                  id="instagram"
                  placeholder="@username"
                  value={formData.socialProfiles.instagram}
                  onChange={(e) => updateFormData('socialProfiles.instagram', e.target.value)}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="twitter">Twitter/X Handle</Label>
                <Input
                  id="twitter"
                  placeholder="@username"
                  value={formData.socialProfiles.twitter}
                  onChange={(e) => updateFormData('socialProfiles.twitter', e.target.value)}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="tiktok">TikTok Handle</Label>
                <Input
                  id="tiktok"
                  placeholder="@username"
                  value={formData.socialProfiles.tiktok}
                  onChange={(e) => updateFormData('socialProfiles.tiktok', e.target.value)}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="youtube">YouTube Channel</Label>
                <Input
                  id="youtube"
                  placeholder="Channel name or URL"
                  value={formData.socialProfiles.youtube}
                  onChange={(e) => updateFormData('socialProfiles.youtube', e.target.value)}
                />
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="status">Current Status</Label>
              <Select value={formData.status} onValueChange={(value) => updateFormData('status', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select your status" />
                </SelectTrigger>
                <SelectContent>
                  {statusOptions[formData.talentType as keyof typeof statusOptions].map((status) => (
                    <SelectItem key={status} value={status}>
                      {status}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-4">
              <div>
                <Label>Achievements & Accolades</Label>
                <p className="text-sm text-muted-foreground mb-3">
                  Add your major achievements, awards, and milestones
                </p>
                <div className="flex gap-2 mb-3">
                  <Input
                    placeholder={achievementPlaceholders[formData.talentType as keyof typeof achievementPlaceholders]}
                    value={newAchievement}
                    onChange={(e) => setNewAchievement(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addAchievement()}
                  />
                  <Button onClick={addAchievement} variant="outline">Add</Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.achievements.map((achievement, index) => (
                    <Badge
                      key={index}
                      variant="secondary"
                      className="cursor-pointer"
                      onClick={() => removeAchievement(index)}
                    >
                      {achievement} ×
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
            
            <div className="space-y-4">
              <h4 className="font-medium">Agent/Manager Contact (Optional)</h4>
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label htmlFor="agentName">Agent Name</Label>
                  <Input
                    id="agentName"
                    placeholder="Agent or manager name"
                    value={formData.agentContact.name}
                    onChange={(e) => updateFormData('agentContact.name', e.target.value)}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="agentEmail">Agent Email</Label>
                  <Input
                    id="agentEmail"
                    type="email"
                    placeholder="agent@example.com"
                    value={formData.agentContact.email}
                    onChange={(e) => updateFormData('agentContact.email', e.target.value)}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="agentPhone">Agent Phone</Label>
                  <Input
                    id="agentPhone"
                    placeholder="(555) 123-4567"
                    value={formData.agentContact.phone}
                    onChange={(e) => updateFormData('agentContact.phone', e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div className="text-center space-y-2">
              <Trophy className="h-12 w-12 text-primary mx-auto" />
              <h3 className="text-lg font-semibold">Review Your Profile</h3>
              <p className="text-muted-foreground">
                Please review all information before creating your profile
              </p>
            </div>
            
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="h-5 w-5" />
                  {formData.name}
                </CardTitle>
                <CardDescription>{formData.sport} • {formData.status}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h4 className="font-medium mb-1">Bio</h4>
                  <p className="text-sm text-muted-foreground">{formData.bio}</p>
                </div>
                
                {formData.location && (
                  <div>
                    <h4 className="font-medium mb-1">Location</h4>
                    <p className="text-sm text-muted-foreground">{formData.location}</p>
                  </div>
                )}
                
                {formData.achievements.length > 0 && (
                  <div>
                    <h4 className="font-medium mb-2">Achievements</h4>
                    <div className="flex flex-wrap gap-1">
                      {formData.achievements.map((achievement, index) => (
                        <Badge key={index} variant="outline" className="text-xs">
                          {achievement}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
                
                <div>
                  <h4 className="font-medium mb-2">Social Profiles</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    {Object.entries(formData.socialProfiles).map(([platform, handle]) => (
                      handle && (
                        <div key={platform}>
                          <span className="font-medium capitalize">{platform}:</span>
                          <p className="text-muted-foreground">{handle}</p>
                        </div>
                      )
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background p-4">
      <div className="container mx-auto max-w-2xl space-y-8">
        {/* Header */}
        <div className="text-center space-y-4 pt-8">
          <h1 className="text-3xl font-bold text-foreground">
            Talent Profile Setup
          </h1>
          <p className="text-muted-foreground">
            Let's create your profile to get started
          </p>
        </div>

        {/* Progress */}
        <div className="space-y-4">
          <Progress value={progress} className="w-full" />
          <div className="flex justify-between text-sm text-muted-foreground">
            {steps.map((step, index) => (
              <div key={step.id} className={`flex items-center gap-1 ${currentStep >= step.id ? 'text-primary' : ''}`}>
                <step.icon className="h-4 w-4" />
                <span className="hidden sm:inline">{step.title}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <Card>
          <CardContent className="pt-6">
            {renderStepContent()}
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex justify-between">
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={currentStep === 1 ? onBack : prevStep}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              {currentStep === 1 ? (isCompletion ? 'Back' : 'Back to Roles') : 'Previous'}
            </Button>
            {isCompletion && (
              <Button 
                variant="ghost" 
                onClick={() => window.location.href = '/profile'}
                className="flex items-center gap-2"
              >
                Return to Profile
              </Button>
            )}
          </div>
          
          {currentStep < steps.length ? (
            <Button onClick={nextStep} className="flex items-center gap-2">
              Next
              <ArrowRight className="h-4 w-4" />
            </Button>
          ) : (
            <Button 
              onClick={handleSubmit} 
              disabled={loading || !formData.name || !formData.sport || !formData.bio}
              className="flex items-center gap-2"
            >
              {loading ? 'Creating Profile...' : 'Complete Setup'}
              <CheckCircle className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};